/**
 * useEditApplication Hook
 *
 * Custom hook for managing the application of auto-redline edits
 * Handles edit status tracking, Word API integration, and error handling
 */

import { useState, useCallback, useRef } from "react";
import { AutoRedlineOutput, EditStatus, EditOperation } from "../../../interfaces/autoRedlineTypes";
/**
 * @deprecated - wordEditOperations.ts is deprecated. This import is only used by
 * the applyEdit() function which is not called by current UI. Consider migrating to
 * applySingleEdit() from wordBatchEditOperations.ts if single-edit functionality needed.
 */
import { applyEditToDocument } from "../services/wordEditOperations";
import { applyEditsSequentially } from "../services/wordSequentialEditService";
import { createLogger } from "../../../utils/logger";

const log = createLogger("useEditApplication");

interface UseEditApplicationProps {
  /** Array of auto-redline outputs */
  autoRedlines: AutoRedlineOutput[];

  /** Callback when edit status changes */
  onEditStatusChange?: (editId: string, status: EditStatus, error?: string) => void;

  /** Callback when edit is successfully applied */
  onEditApplied?: (editId: string) => void;

  /** Callback when edit fails */
  onEditFailed?: (editId: string, error: string) => void;
}

interface UseEditApplicationReturn {
  /** Apply a single edit by ID */
  applyEdit: (editId: string) => Promise<boolean>;

  /** Apply all pending edits */
  applyAllEdits: () => Promise<void>;

  /** Get status of a specific edit */
  getEditStatus: (editId: string) => EditStatus;

  /** Get error message for a specific edit */
  getEditError: (editId: string) => string | undefined;

  /** Check if any edit is currently applying */
  isAnyEditApplying: () => boolean;

  /** Reset all edit statuses to pending */
  resetStatuses: () => void;
}

/**
 * Hook for managing edit application
 */
export function useEditApplication({
  autoRedlines,
  onEditStatusChange,
  onEditApplied,
  onEditFailed,
}: UseEditApplicationProps): UseEditApplicationReturn {
  // Track edit statuses
  const [editStatuses, setEditStatuses] = useState<Map<string, EditStatus>>(new Map());
  const [editErrors, setEditErrors] = useState<Map<string, string>>(new Map());

  // Track if any edit is currently applying
  const applyingEdits = useRef<Set<string>>(new Set());

  // Find the AutoRedlineOutput and EditOperation for a given editId
  const findEditAndRedline = useCallback(
    (
      editId: string
    ): {
      redline?: AutoRedlineOutput;
      edit?: EditOperation;
    } => {
      for (const redline of autoRedlines) {
        const edit = redline.edits.find((e) => e.editId === editId);
        if (edit) {
          return { redline, edit };
        }
      }
      return {};
    },
    [autoRedlines]
  );

  // Update edit status
  const updateEditStatus = useCallback(
    (editId: string, status: EditStatus, error?: string) => {
      setEditStatuses((prev) => {
        const newMap = new Map(prev);
        newMap.set(editId, status);
        return newMap;
      });

      if (error) {
        setEditErrors((prev) => {
          const newMap = new Map(prev);
          newMap.set(editId, error);
          return newMap;
        });
      } else {
        setEditErrors((prev) => {
          const newMap = new Map(prev);
          newMap.delete(editId);
          return newMap;
        });
      }

      // Track applying status
      if (status === "applying") {
        applyingEdits.current.add(editId);
      } else {
        applyingEdits.current.delete(editId);
      }

      // Call status change callback
      if (onEditStatusChange) {
        onEditStatusChange(editId, status, error);
      }

      // Call specific callbacks
      if (status === "applied" && onEditApplied) {
        onEditApplied(editId);
      } else if (status === "failed" && error && onEditFailed) {
        onEditFailed(editId, error);
      }
    },
    [onEditStatusChange, onEditApplied, onEditFailed]
  );

  // Apply a single edit
  const applyEdit = useCallback(
    async (editId: string): Promise<boolean> => {
      // Check if already applying
      if (applyingEdits.current.has(editId)) {
        log.warn("Edit is already being applied", { editId });
        return false;
      }

      // Find the edit and redline
      const { redline, edit } = findEditAndRedline(editId);
      if (!redline || !edit) {
        log.error("Edit not found", { editId });
        updateEditStatus(editId, "failed", "Edit not found");
        return false;
      }

      log.info("Applying edit", {
        editId,
        searchPhrase: edit.searchPhrase,
        position: edit.position,
      });

      // Update status to applying
      updateEditStatus(editId, "applying");

      try {
        // Apply the edit using Word service
        const result = await applyEditToDocument(edit, redline.searchParagraph);

        if (result.success) {
          log.info("Edit applied successfully", {
            editId,
            duration: result.duration,
          });
          updateEditStatus(editId, "applied");
          return true;
        } else {
          log.error("Edit application failed", {
            editId,
            error: result.error,
          });
          updateEditStatus(editId, "failed", result.error);
          return false;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        log.error("Exception while applying edit", {
          editId,
          error: errorMessage,
        });
        updateEditStatus(editId, "failed", errorMessage);
        return false;
      }
    },
    [findEditAndRedline, updateEditStatus]
  );

  // Apply all pending edits
  const applyAllEdits = useCallback(async () => {
    // Group pending edits by their redline for batch processing
    const pendingEditsByRedline = new Map<AutoRedlineOutput, string[]>();

    autoRedlines.forEach((redline) => {
      const pendingIds: string[] = [];
      redline.edits.forEach((edit) => {
        const status = editStatuses.get(edit.editId) || "pending";
        if (status === "pending") {
          pendingIds.push(edit.editId);
        }
      });
      if (pendingIds.length > 0) {
        pendingEditsByRedline.set(redline, pendingIds);
      }
    });

    log.info("Applying pending edits sequentially", {
      redlineCount: pendingEditsByRedline.size,
      totalEdits: Array.from(pendingEditsByRedline.values()).reduce(
        (sum, ids) => sum + ids.length,
        0
      ),
    });

    // Apply edits for each redline using sequential operation
    for (const [redline, editIds] of Array.from(pendingEditsByRedline)) {
      try {
        // Use sequential function to handle dependencies between edits
        const results = await applyEditsSequentially(redline, editIds);

        // Process results and update statuses
        results.forEach((result) => {
          if (result.success) {
            updateEditStatus(result.editId, "applied");
          } else {
            updateEditStatus(result.editId, "failed", result.error);
          }
        });
      } catch (error) {
        log.error("Failed to apply batch edits", {
          sectionName: redline.sectionName,
          error: error instanceof Error ? error.message : String(error),
        });

        // Mark all edits in this batch as failed
        editIds.forEach((editId) => {
          const errorMessage =
            error instanceof Error ? error.message : "Sequential operation failed";
          updateEditStatus(editId, "failed", errorMessage);
        });
      }

      // Small delay between redlines for UX
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }, [autoRedlines, editStatuses, updateEditStatus]);

  // Get edit status
  const getEditStatus = useCallback(
    (editId: string): EditStatus => {
      return editStatuses.get(editId) || "pending";
    },
    [editStatuses]
  );

  // Get edit error
  const getEditError = useCallback(
    (editId: string): string | undefined => {
      return editErrors.get(editId);
    },
    [editErrors]
  );

  // Check if any edit is applying
  const isAnyEditApplying = useCallback((): boolean => {
    return applyingEdits.current.size > 0;
  }, []);

  // Reset all statuses
  const resetStatuses = useCallback(() => {
    setEditStatuses(new Map());
    setEditErrors(new Map());
    applyingEdits.current.clear();
  }, []);

  return {
    applyEdit,
    applyAllEdits,
    getEditStatus,
    getEditError,
    isAnyEditApplying,
    resetStatuses,
  };
}
